// /src/pages/GamerIdentityCheck.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../authContext';
import { bootstrapAuth } from '../../src/bootstrapAuth';
import { useSecurity } from '../SecurityContext';
import { useWebsiteAccessGate } from '../hooks/useWebsiteAccessGate';
import {
    isIdentityCheckComplete,
    siteAccessBypassActive,
    guestAccessBypassActive,
    superAdminOnboardingBypassActive,
} from '../utils/onboardingStorage';
import { allowsViewAsMutations } from '../utils/runtimeEnv';
import { getOrgMembershipNotice } from '../utils/orgMembershipMessages';
import LoadingSpinner from '../components/LoadingSpinner';
import GcuAcceptation from './publicTabs/GcuAcceptation';
import {
    fetchMyGuestAccessStatus,
    requestGuestAccess,
} from './guestAccess/guestAccessApi';
import useGuestAccessRequestsSocket from '../hooks/useGuestAccessRequestsSocket';
import discordIcon from '../../src/medias/discord-white.png';
import rsiIcon from '../../src/medias/rsi_white.png';
import copyIcon from '../../src/medias/copy.svg';
import copiedIcon from '../../src/medias/copied.svg';

function IconEnvelope({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M4 8l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconCheckTiny({ className }) {
    return (
        <svg className={className} width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M13.25 4.75L6.125 11.875L2.75 8.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconShieldTrusted({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M12 3L20 6v6c0 4.5-3.25 8.25-8 9-4.75-.75-8-4.5-8-9V6l8-3z"
                stroke="currentColor"
                strokeWidth="1.35"
                strokeLinejoin="round"
            />
            <path d="M9 12.5l1.75 1.75L15 10" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconUserBadge({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <circle cx="12" cy="9" r="4" stroke="currentColor" strokeWidth="1.35" />
            <path d="M5 21v-.5a7 7 0 0114 0v.5" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
        </svg>
    );
}

function IconShieldAction({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M12 21s7-4.35 7-10V6l-7-3-7 3v5c0 5.65 7 10 7 10z"
                stroke="currentColor"
                strokeWidth="1.35"
                strokeLinejoin="round"
            />
            <path d="M12 11v6M9 13.5l3 2 3-2" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconChevronUp({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M17 14l-5-5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconGuideMonitor({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M4 5h16v10H10L7 17v-2H4V5z"
                stroke="currentColor"
                strokeWidth="1.35"
                strokeLinejoin="round"
            />
            <path d="M9 13l2 1 2-3" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconGuideDoc({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M7 4h10l3 3v13a1 1 0 01-1 1H7a2 2 0 01-2-2V6a2 2 0 012-2z"
                stroke="currentColor"
                strokeWidth="1.35"
                strokeLinejoin="round"
            />
            <path d="M8 10h10M8 14h10" stroke="currentColor" strokeWidth="1.15" strokeLinecap="round" />
        </svg>
    );
}

function IconGuideCode({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path d="M8 17l-4-4 4-4M16 9l4 4-4 4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14 7l-3 13" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
        </svg>
    );
}

function IconShieldRing({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <path
                d="M12 3L20 6v6c0 4.5-3.25 8.25-8 9-4.75-.75-8-4.5-8-9V6l8-3z"
                stroke="currentColor"
                strokeWidth="1.35"
                strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.35" />
        </svg>
    );
}

/** Furthest RSI step illuminated (1–4). Step n is accented when n ≤ this value. */
function useRsiStepReached(handleTrim, handleToken) {
    return useMemo(() => {
        if (!handleTrim) return 1;
        if (!handleToken) return 2;
        return 4;
    }, [handleTrim, handleToken]);
}

export default function IdentityCheck() {
    const { i18n } = useTranslation();
    const {
        email: authEmail,
        gcuAccepted: authGcuAccepted,
        handles: authHandles,
        setEmail,
        setLanguage,
        discordName: authDiscordName,
        setIsLoggedIn,
        injectAuth,
        userId: authUserId,
    } = useAuth();
    const { isSuperAdmin, isGuest, refreshPermissions, viewAsUserId, uiHasPermission, startViewAs } = useSecurity?.() || {};
    const canWatchGuestAdminWs = Boolean(
      uiHasPermission?.('private.navbar.guestAccessRequests', 'read'),
    );
    const {
        statusLoading: gateLoading,
        viewAsPreview,
        effectiveIdentity,
        onboardingComplete,
        orgMembership,
        orgMembershipEligible,
        subjectIdentity,
        subjectIdentityReady,
        refetchStatus,
    } = useWebsiteAccessGate();

    const displayEmail = viewAsPreview ? effectiveIdentity.email : authEmail;
    const displayHandles = viewAsPreview ? effectiveIdentity.handles : authHandles;
    const displayGcuAccepted = viewAsPreview ? effectiveIdentity.gcuAccepted : authGcuAccepted;
    const displayDiscordName = viewAsPreview ? effectiveIdentity.discordName : authDiscordName;
    const canMutateSubjectGcu = viewAsPreview && allowsViewAsMutations();
    const gcuActionsEnabled = !viewAsPreview || canMutateSubjectGcu;
    const canMutateSubjectGuest = viewAsPreview && allowsViewAsMutations();
    const guestActionsEnabled = !viewAsPreview || canMutateSubjectGuest;
    const [handleConfirmStatus, setHandleConfirmStatus] = useState(null);
    const [handleConfirmMessage, setHandleConfirmMessage] = useState('');
    const [discordUser, setDiscordUser] = useState(null);
    const [discordUnlinkBusy, setDiscordUnlinkBusy] = useState(false);
    const [rsiPanelExpanded, setRsiPanelExpanded] = useState(false);
    const [showGcuModal, setShowGcuModal] = useState(false);
    const [handle, setHandle] = useState('');
    const [handleToken, setHandleToken] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [gcuVersionNumber, setGcuVersionNumber] = useState(null);
    const [showAnonymizationModal, setShowAnonymizationModal] = useState(false);
    const [guestStatus, setGuestStatus] = useState(null);
    const [guestBusy, setGuestBusy] = useState(false);
    const guestRedirectingRef = useRef(false);
    const navigate = useNavigate();

    const handleTrim = handle.trim();
    const rsiStepReached = useRsiStepReached(handleTrim, handleToken);

    const discordConnected = viewAsPreview
        ? Boolean(effectiveIdentity?.discordId || effectiveIdentity?.discordName || subjectIdentity?.discordId || subjectIdentity?.discordName)
        : Boolean(discordUser?.discordId);
    const discordDisplayName = viewAsPreview
        ? (effectiveIdentity?.discordName || subjectIdentity?.discordName || displayDiscordName || '')
        : (discordUser?.discordName || displayDiscordName || '');
    const discordAvatarUrl = viewAsPreview
        ? (effectiveIdentity?.discordAvatar || subjectIdentity?.discordAvatar)
        : discordUser?.discordAvatar;
    const hasDisplayEmail = Boolean(displayEmail && String(displayEmail).trim());
    const displayEmailVerified = viewAsPreview
        ? Boolean(effectiveIdentity?.emailVerified ?? subjectIdentity?.emailVerified)
        : true;
    const showAnonymizationScheduledHint = viewAsPreview
        && hasDisplayEmail
        && Boolean(effectiveIdentity?.anonymizationScheduledAt ?? subjectIdentity?.anonymizationScheduledAt);
    const hasRsiHandles = displayHandles && displayHandles.length > 0;
    const rsiDisplayHandles = hasRsiHandles ? displayHandles.map((h) => h.handle).join(', ') : '';

    const stepAccent = (n) => n <= rsiStepReached;

    const openAnonymizationModal = () => {
        setShowAnonymizationModal(true);
    };

    const closeAnonymizationModal = () => {
        setShowAnonymizationModal(false);
    };

    const handleBackToGcu = () => {
        closeAnonymizationModal();
        setShowGcuModal(true);
    };

    const resetRsiWizard = () => {
        setHandle('');
        setHandleToken('');
        setIsCopied(false);
        setHandleConfirmStatus(null);
        setHandleConfirmMessage('');
    };

    const collapseRsiPanel = () => {
        setRsiPanelExpanded(false);
    };

    const expandRsiPanel = () => {
        setHandleConfirmStatus(null);
        setHandleConfirmMessage('');
        setRsiPanelExpanded(true);
    };

    console.log('[GamerIdentityCheck] gcuVersionNumber : ', gcuVersionNumber);

    const logout = async () => {
        try {
            await axios.post('/api/logout', { withCredentials: true });
        } catch (error) {
            console.error('Failed to log out on the server side', error);
        }

        setIsLoggedIn(false);
        setEmail(null);
        setLanguage('en');
        i18n.changeLanguage('en');
        navigate('/');
    };

    const checkAndRedirect = () => {
        // View-as is a read-only preview of the subject's identity hub — never auto-leave.
        // (Viewer org/onboarding eligibility must not unlock the subject.)
        if (viewAsPreview) return;

        const identity = {
            email: authEmail,
            discordName: authDiscordName,
            handles: authHandles,
            gcuAccepted: authGcuAccepted,
        };
        if (!isIdentityCheckComplete(identity)) return;

        // Wait until org membership status is known before leaving this page.
        // Guests / superadmin bypass may leave without an org membership row.
        const accessBypass = siteAccessBypassActive({ isSuperAdmin, isGuest });
        if (orgMembership == null && !accessBypass) return;

        if (!orgMembershipEligible && !accessBypass) {
            return;
        }

        if (accessBypass || onboardingComplete) {
            if (guestAccessBypassActive(isGuest) && !superAdminOnboardingBypassActive(isSuperAdmin)) {
                sessionStorage.removeItem('initialPath');
                navigate('/members/calendar', { replace: true });
                return;
            }
            const initialPath = sessionStorage.getItem('initialPath') || '/members/personalhub';
            sessionStorage.removeItem('initialPath');
            navigate(initialPath, { replace: true });
            return;
        }
        navigate('/members/onboarding', { replace: true });
    };

    useEffect(() => {
        if (gateLoading) return;
        checkAndRedirect();
    }, [
        gateLoading,
        authEmail,
        authHandles,
        authDiscordName,
        authGcuAccepted,
        isSuperAdmin,
        isGuest,
        onboardingComplete,
        orgMembership,
        orgMembershipEligible,
        viewAsPreview,
    ]);

    useEffect(() => {
        if (displayGcuAccepted === false) {
            setShowGcuModal(true);
        } else {
            setShowGcuModal(false);
        }
    }, [displayGcuAccepted]);

    useEffect(() => {
        if (viewAsPreview) return undefined;

        const fetchDiscordUser = async () => {
            try {
                const response = await axios.post(
                    '/api/auth/discord/user',
                    {},
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        withCredentials: true,
                    }
                );
                if (response.data) {
                    setDiscordUser(response.data);
                    const { user } = await bootstrapAuth();
                    if (user) injectAuth(user);
                } else {
                    setDiscordUser(null);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des données Discord:', error);
                setDiscordUser(null);
            }
        };

        fetchDiscordUser();
        return undefined;
    }, [viewAsPreview]);

    useEffect(() => {
        if (!hasRsiHandles) return;
        setRsiPanelExpanded(false);
        resetRsiWizard();
    }, [hasRsiHandles]);

    const handleDiscordAuthentication = async () => {
        try {
            const response = await axios.post('/api/auth/discord', {}, { withCredentials: true });
            if (response.data.url) {
                window.location.href = response.data.url;
            } else {
                console.error('No redirect URL received.');
            }
        } catch (error) {
            console.error('Error connecting to Discord:', error);
        }
    };

    const handleDiscordDisconnect = async () => {
        if (discordUnlinkBusy) return;
        setDiscordUnlinkBusy(true);
        try {
            await axios.post('/api/auth/discord/unlink', {}, { withCredentials: true });
            setDiscordUser(null);
            const { user } = await bootstrapAuth();
            if (user) injectAuth(user);
        } catch (error) {
            console.error('[disconnect Discord]', error);
        } finally {
            setDiscordUnlinkBusy(false);
        }
    };

    const handleInputChange = (e) => {
        setHandle(e.target.value);
        setHandleToken('');
        setIsCopied(false);
    };

    const submitHandle = async () => {
        if (isSubmitting || !handleTrim) return;
        setIsSubmitting(true);

        try {
            const res = await axios.post('/api/handle/submit', { handle }, { withCredentials: true });

            setIsCopied(false);
            setHandleToken(res.data.tempToken);

            const { user } = await bootstrapAuth();
            injectAuth(user);
        } catch (e) {
            console.error('[submitHandle]', e?.response?.data || e.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const buildHandleConfirmAccessMessage = (orgAccess) => {
        if (!orgAccess || orgAccess.applicable === false) {
            return 'Handle successfully confirmed.';
        }
        if (orgAccess.ok) {
            return 'Handle confirmed. Your rank and org access groups are set up.';
        }
        const issues = [];
        if (orgAccess.rankOk === false) {
            issues.push('rank');
        }
        if (orgAccess.groupsOk === false) {
            issues.push('access groups');
        }
        if (!issues.length) {
            return 'Handle confirmed, but org access setup could not be verified. Please try again shortly.';
        }
        return `Handle confirmed, but your ${issues.join(' and ')} could not be set up yet. Please try again in a few minutes or contact staff if this persists.`;
    };

    const confirmHandle = async () => {
        if (isSubmitting || !handleToken) return;
        setIsSubmitting(true);
        setHandleConfirmStatus(null);
        setHandleConfirmMessage('');

        try {
            const res = await axios.post('/api/handle/confirm', { tempToken: handleToken }, { withCredentials: true });

            if (res.status === 200 && res.data?.message === 'handleConfirmationSucceed') {
                const orgAccess = res.data?.orgAccess ?? null;
                const accessVerified = !orgAccess?.applicable || orgAccess.ok === true;

                setHandleConfirmStatus(accessVerified ? 'success' : 'warning');
                setHandleConfirmMessage(buildHandleConfirmAccessMessage(orgAccess));

                const { user } = await bootstrapAuth();
                injectAuth(user);
                refetchStatus();

                setTimeout(() => {
                    collapseRsiPanel();
                    resetRsiWizard();
                }, accessVerified ? 1200 : 4000);
            } else {
                throw new Error('handleConfirmationFailed');
            }
        } catch (e) {
            console.error('[confirmHandle]', e?.response?.data || e.message);
            setHandleConfirmStatus('error');
            setHandleConfirmMessage('Handle verification failed.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const copyToClipboard = () => {
        if (!handleToken || !navigator.clipboard) return;
        navigator.clipboard
            .writeText(handleToken)
            .then(() => {
                setIsCopied(true);

                setTimeout(() => {
                    setIsCopied(false);
                }, 5000);
            })
            .catch((err) => {
                console.error('Could not copy text: ', err);
            });
    };

    const closeGcuModal = () => {
        setShowGcuModal(false);
        openAnonymizationModal();
    };

    const handleAcceptGcu = async () => {
        if (!gcuActionsEnabled) return;
        try {
            const res = await axios.post('/api/user/gcu-accept', { gcuVersion: gcuVersionNumber }, { withCredentials: true });

            if (res.data?.success) {
                setShowGcuModal(false);

                if (viewAsPreview) {
                    await refetchStatus();
                    return;
                }

                const { user } = await bootstrapAuth();
                injectAuth(user);
            }
        } catch (e) {
            console.error('[handleAcceptGcu]', e?.response?.data || e.message);
        }
    };

    const handleDeclineGcu = async () => {
        if (!gcuActionsEnabled) return;
        try {
            const response = await axios.post(
                `/api/user/gcu-decline`,
                { gcuVersion: gcuVersionNumber },
                { withCredentials: true }
            );
            if (response.data.success) {
                setShowAnonymizationModal(false);
                setShowGcuModal(false);
                if (viewAsPreview) {
                    await refetchStatus();
                    return;
                }
                logout();
            }
        } catch (error) {
            console.error("Erreur lors de l'acceptation des CGU:", error);
        }
    };

    const CountdownTimer = ({ initialMinutes = 20 }) => {
        const [time, setTime] = useState(initialMinutes * 60);

        useEffect(() => {
            const timer = setInterval(() => {
                setTime((prevTime) => {
                    if (prevTime <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }, []);

        const minutes = Math.floor(time / 60);
        const seconds = time % 60;

        return (
            <>
                {minutes} minutes, {seconds} seconds
            </>
        );
    };

    const websiteRegister = () => {
        if (viewAsPreview) return;
        const identityCheck = authEmail && authHandles?.length > 0 && authDiscordName;
        axios.post('/api/auth/user/identity-check', { identityCheck }, { withCredentials: true }).catch((error) => {
            console.error('Error updating Discord tags:', error);
        });
    };

    useEffect(() => {
        websiteRegister();
    }, [viewAsPreview, authEmail, authHandles, authDiscordName]);

    const guideBullets = [
        <IconGuideMonitor key="g1" className="identity-hub-rsi-step__icon-glyph identity-hub-rsi-step__icon-glyph--lg" />,
        <IconGuideDoc key="g2" className="identity-hub-rsi-step__icon-glyph identity-hub-rsi-step__icon-glyph--lg" />,
        <IconGuideCode key="g3" className="identity-hub-rsi-step__icon-glyph identity-hub-rsi-step__icon-glyph--lg" />,
        <IconShieldRing key="g4" className="identity-hub-rsi-step__icon-glyph identity-hub-rsi-step__icon-glyph--lg" />,
    ];

    const guideCopy = [
        {
            title: 'Visit your RSI profile',
            body: (
                <>
                    Go to the RSI website and open your{' '}
                    <a
                        href="https://robertsspaceindustries.com/account/profile"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="identity-hub-rsi-guide-link"
                    >
                        profile page
                    </a>
                    .
                </>
            ),
        },
        {
            title: 'Open the Short Bio field',
            body: <>Scroll to the <strong>Short Bio</strong> section on your profile.</>,
        },
        {
            title: 'Paste your token',
            body: <>In the RSI website in the "profile" menu, Paste the token you generated at the end of the <strong>Short Bio</strong>.</>,
        },
        {
            title: 'Save and confirm here',
            body: <>Save your RSI profile, then return here and complete verification.</>,
        },
    ];

    const orgMembershipNotice = hasRsiHandles && !orgMembershipEligible
        ? getOrgMembershipNotice(orgMembership)
        : null;

    const refreshGuestStatus = async () => {
        try {
            const data = await fetchMyGuestAccessStatus({
                forViewAsSubject: Boolean(viewAsPreview),
            });
            if (data?.ok) setGuestStatus(data);
            return data;
        } catch (err) {
            console.error('[GamerIdentityCheck] guest status failed', err);
            return null;
        }
    };

    const redirectGuestToCalendar = async () => {
        if (guestRedirectingRef.current) return;
        guestRedirectingRef.current = true;
        let guestConfirmed = false;
        try {
            if (!viewAsPreview && typeof refreshPermissions === 'function') {
                // PrivateRoute gates calendar on isGuest bypass — wait for SecGroup Guest.
                for (let i = 0; i < 20; i += 1) {
                    const perms = await refreshPermissions();
                    if (perms?.isGuest) {
                        guestConfirmed = true;
                        break;
                    }
                    await new Promise((r) => setTimeout(r, 300));
                }
                try {
                    const { user, permissions } = await bootstrapAuth();
                    if (user) injectAuth(user);
                    // bootstrapAuth may see Guest before SecurityContext has committed.
                    if (permissions?.isGuest && typeof refreshPermissions === 'function') {
                        const perms = await refreshPermissions();
                        if (perms?.isGuest) guestConfirmed = true;
                    }
                } catch {
                    // ignore
                }
            } else if (viewAsPreview && viewAsUserId != null && typeof startViewAs === 'function') {
                // Reload subject permissions so viewAsIsGuest becomes true before PrivateRoute.
                await startViewAs(viewAsUserId);
                await refetchStatus();
                guestConfirmed = true;
            }

            // Let React commit isGuest before PrivateRoute evaluates the next screen.
            await new Promise((r) => setTimeout(r, 50));

            if (!guestConfirmed && !viewAsPreview) {
                console.warn(
                    '[GamerIdentityCheck] Guest SecGroup not confirmed yet — redirecting with grace state',
                );
            }

            navigate('/members/calendar', {
                replace: true,
                state: { guestAccessGranted: true },
            });

            // If PrivateRoute bounced us back, unlock retries (do not stick forever).
            window.setTimeout(() => {
                const path = String(window.location?.pathname || '').toLowerCase();
                if (path.includes('gameridentitycheck')) {
                    guestRedirectingRef.current = false;
                }
            }, 1500);
        } catch (err) {
            console.error('[GamerIdentityCheck] guest redirect failed', err);
            guestRedirectingRef.current = false;
        }
    };

    useEffect(() => {
        if (gateLoading) return;
        if (viewAsPreview && !subjectIdentityReady) return;
        if (!hasRsiHandles || orgMembershipEligible) {
            setGuestStatus(null);
            return;
        }
        void refreshGuestStatus();
    }, [viewAsPreview, gateLoading, subjectIdentityReady, hasRsiHandles, orgMembershipEligible]);

    // Poll while pending so acceptance still unlocks calendar if the live WS is missed (local Redis hiccup).
    useEffect(() => {
        if (gateLoading) return;
        if (!hasRsiHandles || orgMembershipEligible) return;
        if (guestStatus?.latest?.status !== 'pending') return undefined;

        const timer = setInterval(() => {
            void (async () => {
                const data = await refreshGuestStatus();
                if (data?.latest?.status === 'accepted') {
                    await redirectGuestToCalendar();
                }
            })();
        }, 3000);

        return () => clearInterval(timer);
    }, [
        gateLoading,
        hasRsiHandles,
        orgMembershipEligible,
        guestStatus?.latest?.status,
        viewAsPreview,
    ]);

    const onGuestWsUpdate = async (payload) => {
        const status = payload?.status;
        const payloadUserId = payload?.userId != null ? Number(payload.userId) : null;

        if (viewAsPreview) {
            if (
                viewAsUserId != null
                && payloadUserId != null
                && payloadUserId !== Number(viewAsUserId)
            ) {
                return;
            }
            await refreshGuestStatus();
            if (status === 'accepted') {
                await redirectGuestToCalendar();
            }
            return;
        }

        if (status === 'accepted') {
            await redirectGuestToCalendar();
            return;
        }
        await refreshGuestStatus();
    };

    const guestSocketEnabled = Boolean(
        hasRsiHandles
        && !orgMembershipEligible
        && (!viewAsPreview || canWatchGuestAdminWs),
    );

    useGuestAccessRequestsSocket({
        enabled: guestSocketEnabled,
        isLoggedIn: true,
        mode: viewAsPreview ? 'admin' : 'self',
        selfUserId: viewAsPreview ? null : authUserId,
        onSelfUpdate: viewAsPreview ? null : onGuestWsUpdate,
        onDatasetUpdate: viewAsPreview ? onGuestWsUpdate : null,
    });

    const submitGuestAccessRequest = async () => {
        if (!guestActionsEnabled || guestBusy) return;
        setGuestBusy(true);
        try {
            const data = await requestGuestAccess({
                forViewAsSubject: Boolean(viewAsPreview),
            });
            if (data?.ok) {
                setGuestStatus((prev) => ({
                    ...(prev || {}),
                    ok: true,
                    canRequest: false,
                    latest: data.latest,
                    rejectionCount: data.rejectionCount ?? prev?.rejectionCount ?? 0,
                }));
            }
            await refreshGuestStatus();
        } catch (err) {
            console.error('[GamerIdentityCheck] guest request failed', err);
            await refreshGuestStatus();
        } finally {
            setGuestBusy(false);
        }
    };

    if (viewAsPreview && (gateLoading || !subjectIdentityReady)) {
        return (
            <div className="identity-hub identity-hub--loading">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div
            className={`identity-hub${viewAsPreview ? ' identity-hub--view-as' : ''}`}
            data-resource="profile.identitycheck"
            data-action="read"
        >
            {viewAsPreview ? (
                <p className="identity-hub__view-as-banner" role="status">
                    View-as preview — member identity check
                    {displayDiscordName ? `: ${displayDiscordName}` : ''}
                    {canMutateSubjectGcu
                        ? ' — GCU / Guest actions apply to this user (non-prod only)'
                        : ' — shown as the user sees it (mutations disabled in production)'}
                </p>
            ) : null}
            <header className="identity-hub__header">
                <h1 className="identity-hub__title">Identity Check</h1>
                <div className="identity-hub__title-rule" aria-hidden>
                    <span className="identity-hub__title-rule-line" />
                    <span className="identity-hub__title-rule-diamond" />
                    <span className="identity-hub__title-rule-line" />
                </div>
                <p className="identity-hub__subtitle">There is a few more steps so we can verify your IDs</p>
            </header>

            <div className={`identity-hub-card ${rsiPanelExpanded && !hasRsiHandles ? 'identity-hub-card--rsi-open' : ''}`}>
                <div className="identity-hub-card__inner">
                    <div className="identity-hub-row identity-hub-row--email identity-hub-row--dual">
                        <div className="identity-hub-row__icon-wrap identity-hub-row__icon-wrap--email">
                            <IconEnvelope />
                        </div>
                        <div className="identity-hub-row__body">
                            <span className="identity-hub-row__label identity-hub-row__label--email">Email</span>
                            {hasDisplayEmail ? (
                                <span className="identity-hub-row__value">{displayEmail}</span>
                            ) : (
                                <span className="identity-hub-row__status identity-hub-row__status--muted">Not set</span>
                            )}
                            {hasDisplayEmail ? (
                                displayEmailVerified ? (
                                    <span className="identity-hub-row__status identity-hub-row__status--success">
                                        <IconCheckTiny />
                                        Verified
                                    </span>
                                ) : (
                                    <span className="identity-hub-row__status identity-hub-row__status--muted">
                                        Present — not verified
                                    </span>
                                )
                            ) : (
                                <span className="identity-hub-row__hint">
                                    An email address is required to complete identity check.
                                </span>
                            )}
                            {showAnonymizationScheduledHint ? (
                                <span className="identity-hub-row__hint">
                                    Account anonymization is scheduled; email is still stored in the database.
                                </span>
                            ) : null}
                        </div>
                    </div>

                    <div className="identity-hub-row identity-hub-row--discord">
                        <div className="identity-hub-row__icon-wrap identity-hub-row__icon-wrap--discord">
                            {discordConnected && discordAvatarUrl ? (
                                <img
                                    src={discordAvatarUrl}
                                    alt=""
                                    className="identity-hub-row__icon-discord-linked"
                                />
                            ) : (
                                <img src={discordIcon} alt="" />
                            )}
                        </div>
                        <div className="identity-hub-row__body">
                            <span className="identity-hub-row__label identity-hub-row__label--discord">Discord</span>
                            {discordConnected ? (
                                <>
                                    <span className="identity-hub-row__value">{discordDisplayName}</span>
                                    <span className="identity-hub-row__status identity-hub-row__status--success">
                                        <IconCheckTiny />
                                        Connected
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="identity-hub-row__status identity-hub-row__status--muted">Not connected</span>
                                    <span className="identity-hub-row__hint">
                                        Connect your Discord account so we can tie your account to your Discord.
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="identity-hub-row__actions">
                            {discordConnected ? (
                                <button
                                    type="button"
                                    className="identity-hub-btn-outline identity-hub-btn-outline--muted"
                                    onClick={handleDiscordDisconnect}
                                    disabled={viewAsPreview || discordUnlinkBusy}
                                >
                                    Disconnect
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="identity-hub-btn-outline identity-hub-btn-outline--discord"
                                    onClick={handleDiscordAuthentication}
                                    disabled={viewAsPreview}
                                >
                                    <img src={discordIcon} alt="" className="identity-hub-discord-mini" />
                                    Connect Discord
                                </button>
                            )}
                        </div>
                    </div>

                    {!hasRsiHandles ? (
                        <section
                            className={`identity-hub-rsi-section ${rsiPanelExpanded ? 'identity-hub-rsi-section--open' : ''}`}
                            aria-label="RSI Handle connection"
                        >
                            <div className="identity-hub-rsi-head">
                                <div className="identity-hub-row__icon-wrap identity-hub-row__icon-wrap--rsi">
                                    <img src={rsiIcon} alt="" />
                                </div>
                                <div className="identity-hub-row__body">
                                    <span className="identity-hub-row__label identity-hub-row__label--rsi">RSI Handle</span>
                                    <span className="identity-hub-row__status identity-hub-row__status--muted">Not connected</span>
                                    <span className="identity-hub-row__hint">
                                        Connect your RSI handle so we know who you are.
                                    </span>
                                </div>
                                <div className="identity-hub-row__actions identity-hub-rsi-head__tail">
                                    {rsiPanelExpanded ? (
                                        <button
                                            type="button"
                                            className="identity-hub-rsi-hide"
                                            onClick={collapseRsiPanel}
                                            disabled={viewAsPreview}
                                        >
                                            Hide details
                                            <IconChevronUp aria-hidden />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="identity-hub-btn-rsi-connect"
                                            onClick={expandRsiPanel}
                                            disabled={viewAsPreview}
                                        >
                                            Connect RSI Handle
                                            <span className="identity-hub-btn-rsi-connect__arrow" aria-hidden>
                                                →
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div
                                className={`identity-hub-rsi-expand ${rsiPanelExpanded ? 'identity-hub-rsi-expand--open' : ''}`}
                                aria-hidden={!rsiPanelExpanded}
                            >
                                <div className="identity-hub-rsi-expand__measure">
                                    <div className="identity-hub-rsi-expand__surface">
                                        <div className="identity-hub-rsi-wizard">
                                            <div className="identity-hub-rsi-wizard__heading-row">
                                                <h2 className="identity-hub-rsi-wizard__legend">Verification steps</h2>
                                                <h2 className="identity-hub-rsi-wizard__legend identity-hub-rsi-wizard__legend--muted">
                                                    How it works on RSI
                                                </h2>
                                            </div>

                                            <div className="identity-hub-rsi-wizard__pair-row">
                                                <div className={`identity-hub-rsi-step ${stepAccent(1) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet">1</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">Enter your RSI handle</h3>
                                                        <div
                                                            className={`identity-hub-rsi-input-shell ${!stepAccent(1) ? 'identity-hub-rsi-input-shell--inactive' : ''}`}
                                                        >
                                                            <IconUserBadge />
                                                            <input
                                                                type="text"
                                                                name="handle"
                                                                className="identity-hub-rsi-textfield"
                                                                placeholder="Enter your RSI handle"
                                                                value={handle}
                                                                onChange={handleInputChange}
                                                                autoComplete="off"
                                                                disabled={viewAsPreview || !rsiPanelExpanded}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={`identity-hub-rsi-step identity-hub-rsi-step--rsi-guide ${stepAccent(1) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet identity-hub-guide-icon">{guideBullets[0]}</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">{guideCopy[0].title}</h3>
                                                        <p className="identity-hub-rsi-step__guide-text">{guideCopy[0].body}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="identity-hub-rsi-wizard__pair-row">
                                                <div className={`identity-hub-rsi-step ${stepAccent(2) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet">2</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">Generate verification token</h3>
                                                        <button
                                                            type="button"
                                                            className={`identity-hub-rsi-btn-primary ${handleTrim ? 'identity-hub-rsi-btn-primary--raised' : ''}`}
                                                            onClick={submitHandle}
                                                            disabled={viewAsPreview || isSubmitting || !handleTrim}
                                                        >
                                                            <IconShieldAction />
                                                            Generate Token
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className={`identity-hub-rsi-step identity-hub-rsi-step--rsi-guide ${stepAccent(2) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet identity-hub-guide-icon">{guideBullets[1]}</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">{guideCopy[1].title}</h3>
                                                        <p className="identity-hub-rsi-step__guide-text">{guideCopy[1].body}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="identity-hub-rsi-wizard__pair-row">
                                                <div className={`identity-hub-rsi-step ${stepAccent(3) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet">3</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">Paste token in your RSI bio</h3>
                                                        <div
                                                            className={`identity-hub-rsi-input-shell identity-hub-rsi-token-row ${!stepAccent(3) ? 'identity-hub-rsi-input-shell--inactive' : ''}`}
                                                        >
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                className="identity-hub-rsi-textfield identity-hub-rsi-textfield--readonly"
                                                                value={
                                                                    handleToken
                                                                        ? handleToken
                                                                        : 'Generate a token above to continue'
                                                                }
                                                                tabIndex={-1}
                                                                aria-live="polite"
                                                            />
                                                            <button
                                                                type="button"
                                                                className={`identity-hub-rsi-copy-hit ${!handleToken ? 'identity-hub-rsi-copy-hit--inactive' : ''}`}
                                                                onClick={copyToClipboard}
                                                                disabled={viewAsPreview || !handleToken || !rsiPanelExpanded}
                                                                title="Copy token"
                                                            >
                                                                <img src={isCopied ? copiedIcon : copyIcon} alt="" />
                                                            </button>
                                                        </div>
                                                        {handleToken ? (
                                                            <div className="identity-hub-rsi-msg identity-hub-rsi-msg--timer">
                                                                <CountdownTimer initialMinutes={20} />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <div className={`identity-hub-rsi-step identity-hub-rsi-step--rsi-guide ${stepAccent(3) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet identity-hub-guide-icon">{guideBullets[2]}</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">{guideCopy[2].title}</h3>
                                                        <p className="identity-hub-rsi-step__guide-text">{guideCopy[2].body}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="identity-hub-rsi-wizard__pair-row">
                                                <div className={`identity-hub-rsi-step ${stepAccent(4) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet">4</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">Confirm verification</h3>
                                                        <button
                                                            type="button"
                                                            className={`identity-hub-rsi-btn-primary ${handleToken ? 'identity-hub-rsi-btn-primary--raised' : ''}`}
                                                            onClick={confirmHandle}
                                                            disabled={
                                                                viewAsPreview
                                                                || isSubmitting
                                                                || !handleToken
                                                            }
                                                            data-resource="profile.identitycheck"
                                                            data-action="write"
                                                            data-route="/api/handle/confirm"
                                                        >
                                                            <IconShieldAction />
                                                            Confirm My Handle
                                                        </button>
                                                        {handleConfirmStatus === 'success' ? (
                                                            <p className="identity-hub-rsi-msg identity-hub-rsi-msg--success">{handleConfirmMessage}</p>
                                                        ) : null}
                                                        {handleConfirmStatus === 'warning' ? (
                                                            <p className="identity-hub-rsi-msg identity-hub-rsi-msg--warning">{handleConfirmMessage}</p>
                                                        ) : null}
                                                        {handleConfirmStatus === 'error' ? (
                                                            <p className="identity-hub-rsi-msg identity-hub-rsi-msg--error">{handleConfirmMessage}</p>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <div className={`identity-hub-rsi-step identity-hub-rsi-step--rsi-guide ${stepAccent(4) ? 'identity-hub-rsi-step--accent' : ''}`}>
                                                    <span className="identity-hub-rsi-step__rail-after" aria-hidden />
                                                    <div className="identity-hub-rsi-step__bullet identity-hub-guide-icon">{guideBullets[3]}</div>
                                                    <div className="identity-hub-rsi-step__body">
                                                        <h3 className="identity-hub-rsi-step__title">{guideCopy[3].title}</h3>
                                                        <p className="identity-hub-rsi-step__guide-text">{guideCopy[3].body}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    ) : (
                        <div className="identity-hub-row identity-hub-row--rsi">
                            <div className="identity-hub-row__icon-wrap identity-hub-row__icon-wrap--rsi">
                                <img src={rsiIcon} alt="" />
                            </div>
                            <div className="identity-hub-row__body">
                                <span className="identity-hub-row__label identity-hub-row__label--rsi">RSI Handle</span>
                                <span className="identity-hub-row__value">{rsiDisplayHandles}</span>
                                <span className="identity-hub-row__status identity-hub-row__status--success">
                                    <IconCheckTiny />
                                    Connected
                                </span>
                            </div>
                        </div>
                    )}

                    {orgMembershipNotice ? (
                        <section
                            className={`identity-hub-membership-notice identity-hub-membership-notice--${orgMembershipNotice.tone}`}
                            aria-live="polite"
                            data-resource="profile.identitycheck.membership"
                            data-action="read"
                        >
                            <h2 className="identity-hub-membership-notice__title">
                                {orgMembershipNotice.title}
                            </h2>
                            <p className="identity-hub-membership-notice__body">
                                {orgMembershipNotice.body}
                            </p>
                            {orgMembershipNotice.ctaUrl ? (
                                <a
                                    className="identity-hub-membership-notice__cta"
                                    href={orgMembershipNotice.ctaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-resource="profile.identitycheck.membership"
                                    data-action="read"
                                >
                                    {orgMembershipNotice.ctaLabel}
                                </a>
                            ) : null}
                            {orgMembershipNotice.guestHint ? (
                                <p className="identity-hub-membership-notice__guest-hint">
                                    {orgMembershipNotice.guestHint}
                                </p>
                            ) : null}
                        </section>
                    ) : null}

                    {hasRsiHandles && !orgMembershipEligible ? (
                        <section
                            className="identity-hub-guest-access"
                            aria-live="polite"
                            data-resource="profile.identitycheck.guestAccess"
                            data-action="read"
                        >
                            <h2 className="identity-hub-guest-access__title">Guest access</h2>
                            {viewAsPreview && !canMutateSubjectGuest ? (
                                <p className="identity-hub-guest-access__status" role="status">
                                    Production view-as: Guest request UI is shown as the member sees it. Submitting is disabled.
                                </p>
                            ) : null}
                            {guestStatus?.latest?.status === 'pending' ? (
                                <p className="identity-hub-guest-access__status identity-hub-guest-access__status--pending">
                                    Status: <strong>Pending</strong> — an officer will review your Guest request.
                                </p>
                            ) : null}
                            {guestStatus?.latest?.status === 'rejected' ? (
                                <p className="identity-hub-guest-access__status identity-hub-guest-access__status--rejected">
                                    Your Guest access request was temporarily refused.
                                    Please contact an officer on Discord if you need clarification, or submit a new request below.
                                </p>
                            ) : null}
                            {guestStatus?.reason === 'identity_incomplete' ? (
                                <p className="identity-hub-guest-access__status">
                                    Guest access requires a complete identity check (email, Discord, RSI handle, and GCU).
                                </p>
                            ) : null}
                            {guestStatus?.canRequest ? (
                                guestActionsEnabled ? (
                                    <button
                                        type="button"
                                        className="identity-hub-guest-access__btn"
                                        onClick={submitGuestAccessRequest}
                                        disabled={guestBusy}
                                        data-resource="profile.identitycheck.guestAccess"
                                        data-action="create"
                                        data-route="/api/user/guest-access/request"
                                    >
                                        {guestBusy ? 'Submitting…' : 'Request a Guest access'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="identity-hub-guest-access__btn"
                                        disabled
                                        data-resource="profile.identitycheck.guestAccess"
                                        data-action="read"
                                    >
                                        Request a Guest access
                                    </button>
                                )
                            ) : null}
                            {guestStatus == null && !orgMembershipEligible ? (
                                <p className="identity-hub-guest-access__status">Loading Guest access status…</p>
                            ) : null}
                        </section>
                    ) : null}
                </div>
            </div>

            <footer className="identity-hub-footer">
                <div className="identity-hub-footer__icon">
                    <IconShieldTrusted />
                </div>
                <p className="identity-hub-footer__copy">
                    This is our customs area.
                </p>
            </footer>

            <Modal show={showGcuModal} onHide={gcuActionsEnabled ? closeGcuModal : undefined} dialogClassName="gcu-modal-content" backdrop="static">
                <Modal.Header closeButton={gcuActionsEnabled} className="gcu-header">
                    <Modal.Title className="gcu-title">General condition of use</Modal.Title>
                </Modal.Header>
                <Modal.Body className="gcu-content">
                    {viewAsPreview && !canMutateSubjectGcu ? (
                        <p className="identity-hub__view-as-gcu-hint" role="status">
                            Production view-as: GCU is shown as the member sees it. Accept/decline is disabled.
                        </p>
                    ) : null}
                    <GcuAcceptation
                        onVersionNumberFetched={(v) => {
                            console.log('[IdentityCheck] Version GCU reçue →', v);
                            setGcuVersionNumber(v);
                        }}
                    />

                    <Modal.Footer closeButton className="gcu-footer">
                        {gcuActionsEnabled ? (
                            <>
                                <Button
                                    onClick={closeGcuModal}
                                    className="btn-style1 delete red"
                                    data-resource="profile.identitycheck.gcu"
                                    data-action="write"
                                    data-route="/api/user/gcu-decline"
                                >
                                    I decline
                                </Button>
                                <Button
                                    onClick={handleAcceptGcu}
                                    className="btn-style1"
                                    data-resource="profile.identitycheck.gcu"
                                    data-action="write"
                                    data-route="/api/user/gcu-accept"
                                >
                                    I accept
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={() => setShowGcuModal(false)}
                                className="btn-style1"
                                data-resource="profile.identitycheck"
                                data-action="read"
                            >
                                Close preview
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal.Body>
            </Modal>

            <Modal
                show={showAnonymizationModal}
                onHide={gcuActionsEnabled ? closeAnonymizationModal : undefined}
                dialogClassName="anonymization-modal-content"
                backdrop="static"
            >
                <Modal.Header closeButton={gcuActionsEnabled} className="anonymization-header">
                    <Modal.Title className="anonymization-title">WARNING</Modal.Title>
                </Modal.Header>
                <Modal.Body className="anonymization-content">
                    <p className="content">
                        Failure to accept the General Terms of Use for Pallas-Invictus.com will result in your account being
                        anonymized after 15 days, unless you return and accept the Terms within this period.
                        <br />
                        <br />
                        Don't hesitate to reach out, so we can understand your concerns.
                    </p>

                    <Modal.Footer className="anonymization-footer">
                        <Button onClick={handleBackToGcu} className="btn-style1">
                            get me Back to the GCU so I can read again
                        </Button>
                        <Button
                            onClick={handleDeclineGcu}
                            className="btn-style1 delete red"
                            disabled={!gcuActionsEnabled}
                            data-resource="profile.identitycheck.gcu"
                            data-action="write"
                            data-route="/api/user/gcu-decline"
                        >
                            I decline - please anonymise my account
                        </Button>
                    </Modal.Footer>
                </Modal.Body>
            </Modal>
        </div>
    );
}
